import { format, isValid } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DateRange,
  DateRangePicker,
} from '@/components/date-range-picker';
import { Button } from '@/components/ui/button';
import { Calendar, CalendarProps } from '@/components/ui/calendar';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { computeEndMonth } from './compute-end-month';
import { MultipleDatePickerField } from './multiple-date-picker-field';

type DatePickerFormProps = {
  form: any;
  fieldName: string;
  label?: string;
  placeholder?: string;
  inline?: boolean;
  description?: string;
  mode?: CalendarProps['mode'];
  align?: React.ComponentProps<typeof PopoverContent>['align'];
} & Omit<CalendarProps, 'mode'>;

/**
 * React-hook-form date picker field. Renders a single-date popover calendar,
 * or delegates to {@link DateRangePicker} in range mode.
 *
 * @param props.form - the react-hook-form instance
 * @param props.fieldName - name of the bound form field
 * @param props.label - optional field label
 * @param props.placeholder - i18n key shown while empty
 * @param props.inline - lay the label and trigger on one row
 * @param props.description - optional field description
 * @param props.mode - calendar mode (single, multiple or range)
 * @param props.align - popover alignment
 * @returns the form-bound date picker
 */
export function DatePickerForm({
  form,
  fieldName,
  label,
  description,
  placeholder = 'datepicker-form.placeholder',
  inline = true,
  mode = 'single',
  align = 'end',
  ...props
}: DatePickerFormProps) {
  const { t } = useTranslation();

  if (mode === 'multiple') {
    return (
      <MultipleDatePickerField
        form={form}
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        inline={inline}
        description={description}
        align={align}
        calendarProps={
          props as Omit<
            Extract<CalendarProps, { mode: 'multiple' }>,
            'mode' | 'selected' | 'onSelect'
          >
        }
      />
    );
  }

  if (mode === 'range') {
    return (
      <FormField
        control={form.control}
        name={fieldName}
        render={({ field }) => (
          <FormItem>
            <div
              className={
                inline
                  ? 'flex flex-row items-center justify-between gap-2 space-y-0'
                  : 'flex flex-col gap-2'
              }
            >
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <DateRangePicker
                  value={(field.value ?? {}) as DateRange}
                  onChange={field.onChange}
                  placeholder={placeholder}
                  align={align === 'center' ? 'center' : align}
                  className={inline ? 'w-full md:w-[260px]' : 'w-full'}
                  calendarProps={{
                    ...(props as Omit<
                      Extract<CalendarProps, { mode: 'range' }>,
                      'mode' | 'selected' | 'onSelect'
                    >),
                    endMonth: computeEndMonth(
                      isValid(field.value?.from) ? field.value.from : null,
                    ),
                    defaultMonth: isValid(field.value?.from)
                      ? field.value.from
                      : props.defaultMonth,
                  }}
                />
              </FormControl>
            </div>

            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <div
            className={
              inline
                ? 'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'
                : 'flex flex-col gap-2'
            }
          >
            {label && <FormLabel>{label}</FormLabel>}
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant={'outline'}
                    className={cn(
                      (inline
                        ? 'w-full sm:w-auto sm:min-w-[200px] md:w-[240px]'
                        : 'w-full') +
                        ' ' +
                        'gap-2 pl-3 text-left font-normal',
                      !field.value && 'text-muted-foreground',
                    )}
                  >
                    {isValid(field.value) ? (
                      <span>{format(field.value, 'PPP')}</span>
                    ) : (
                      <span>{t(placeholder)}</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align={align}
                alignOffset={20}
              >
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={(value: any) => {
                    (field.onChange as any)(value);
                  }}
                  autoFocus={true}
                  {...props}
                  endMonth={computeEndMonth(
                    isValid(field.value) ? field.value : null,
                  )}
                  defaultMonth={
                    (isValid(field.value) ? field.value : null) ??
                    props.defaultMonth
                  }
                />
              </PopoverContent>
            </Popover>
          </div>

          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
